export const DefaultScanRecvRatio = 1;

export enum PoRecvActionType {
  SetCrossWarehouseReceive,
  SetCurrentLocation,
  SetCurrentPo,
  SetDefaultLocation,
  SetEnableLot,
  SetEnableMultiPurposeCode,
  SetEnablePOReceiveControl,
  SetEnablePOReceiveExtra,
  SetEnableUom,
  SetIsCheckingUom,
  SetLockScreen,
  SetMatchedPatternList,
  SetNextPatternCode,
  SetPatternCode,
  SetPoLines,
  SetPoList,
  SetPoOverRecvPercent,
  SetRecvItems,
  SetRecvStep,
  SetSavedItems,
  SetScanRecvCountType,
  SetScanRecvLotPolicy,
  SetScanRecvLotUniNum,
  SetScanRecvRatio,
  SetScanRecvRatioType,
  SetScanRecvScopeType,
  SetSelectedWarehouse,
  SetTimeZone,
  SetUomInfo,
};

export enum PoRecvStep {
  PoInfo = 'poInfo',
  RecvPo = 'recvPo',
  ScanPo = 'scanPo',
};

export enum ScanRecvCountType {
  Default,
  Custom,
};

export enum ScanRecvLotPolicy {
  InputManually,
  AssignSameLot,
};

export enum ScanRecvRatioType {
  FixedRatio,
  Unreceived,
};

export enum ScanRecvScopeType {
  CurrentPo,
  AllPo,
};

type PoRecvAction = {
  type: PoRecvActionType,
  params: any,
};

type PoRecvState = {
  crossWarehouseReceive: string;
  currentLocation: StringKAnyVPair;
  currentPo: StringKAnyVPair;
  defaultLocation: string;
  enableLot: boolean;
  enableMultiPurposeCode: boolean;
  enablePOReceiveControl: boolean;
  enablePOReceiveExtra: string;
  enableUom: boolean;
  isCheckingUom: boolean;
  lockScreen: boolean;
  matchedPatternList: StringKAnyVPair[];
  nextPatternCode: string;
  patternCode: StringKAnyVPair;
  poLines: StringKAnyVPair[];
  poList: StringKAnyVPair[];
  poOverRecvPercent: number;
  recvItems: StringKAnyVPair[];
  recvStep: PoRecvStep;
  savedItems: StringKAnyVPair[];
  scanRecvCountType: ScanRecvCountType;
  scanRecvLotPolicy: ScanRecvLotPolicy;
  scanRecvLotUniNum: string;
  scanRecvRatio: number;
  scanRecvRatioType: ScanRecvRatioType;
  scanRecvScopeType: ScanRecvScopeType;
  selectedWarehouse: StringKAnyVPair;
  timeZone: string;
  uomInfo: UomInfo;
};

type UomInfo = {
  uom: string,
  uomRate: number,
};

export const defaultUom: UomInfo = { uom: 'EA', uomRate: 1 };

export const initialState = (state: StringKAnyVPair) => ({
  crossWarehouseReceive: '0',
  currentLocation: {},
  currentPo: {},
  defaultLocation: '',
  enableLot: false,
  enableMultiPurposeCode: false,
  enablePOReceiveControl: false,
  enablePOReceiveExtra: '0',
  enableUom: false,
  isCheckingUom: false,
  lockScreen: false,
  matchedPatternList: [],
  nextPatternCode: '',
  patternCode: {},
  poLines: [],
  poList: [],
  poOverRecvPercent: 0,
  recvItems: [],
  recvStep: PoRecvStep.ScanPo,
  savedItems: [],
  scanRecvCountType: ScanRecvCountType.Default,
  scanRecvLotPolicy: ScanRecvLotPolicy.InputManually,
  scanRecvLotUniNum: '',
  scanRecvRatio: DefaultScanRecvRatio,
  scanRecvRatioType: ScanRecvRatioType.FixedRatio,
  scanRecvScopeType: ScanRecvScopeType.AllPo,
  selectedWarehouse: {},
  timeZone: '',
  uomInfo: defaultUom,
  ...state,
});

export const isValidStep = (val: any) => Object.values(PoRecvStep).includes(val as PoRecvStep);

export const reducer = (state: PoRecvState, action: PoRecvAction) => {
  switch (action.type) {
    case PoRecvActionType.SetCrossWarehouseReceive:
      return { ...state, crossWarehouseReceive: action.params };

    case PoRecvActionType.SetCurrentLocation:
      return { ...state, currentLocation: { ...action.params } };

    case PoRecvActionType.SetCurrentPo:
      return { ...state, currentPo: { ...action.params } };

    case PoRecvActionType.SetDefaultLocation:
      return { ...state, defaultLocation: action.params };

    case PoRecvActionType.SetEnableLot:
      return { ...state, enableLot: action.params };

    case PoRecvActionType.SetEnableMultiPurposeCode:
      return { ...state, enableMultiPurposeCode: action.params };

    case PoRecvActionType.SetEnablePOReceiveControl:
      return { ...state, enablePOReceiveControl: action.params };

    case PoRecvActionType.SetEnablePOReceiveExtra:
      return { ...state, enablePOReceiveExtra: action.params };

    case PoRecvActionType.SetEnableUom:
      return { ...state, enableUom: action.params };

    case PoRecvActionType.SetIsCheckingUom:
      return { ...state, isCheckingUom: action.params };

    case PoRecvActionType.SetLockScreen:
      return { ...state, lockScreen: action.params };

    case PoRecvActionType.SetMatchedPatternList:
      return { ...state, matchedPatternList: [ ...action.params ] };

    case PoRecvActionType.SetNextPatternCode:
      return { ...state, nextPatternCode: action.params };

    case PoRecvActionType.SetPatternCode:
      return { ...state, patternCode: { ...action.params } };

    case PoRecvActionType.SetPoLines:
      return { ...state, poLines: [ ...action.params ] };

    case PoRecvActionType.SetPoList:
      return { ...state, poList: [ ...action.params ] };

    case PoRecvActionType.SetPoOverRecvPercent:
      return { ...state, poOverRecvPercent: action.params };

    case PoRecvActionType.SetRecvItems:
      return { ...state, recvItems: action.params };

    case PoRecvActionType.SetRecvStep:
      return { ...state, recvStep: action.params };

    case PoRecvActionType.SetSavedItems:
      return { ...state, savedItems: [ ...action.params ] };

    case PoRecvActionType.SetScanRecvCountType:
      return { ...state, scanRecvCountType: action.params };

    case PoRecvActionType.SetScanRecvLotPolicy:
      return { ...state, scanRecvLotPolicy: action.params };

    case PoRecvActionType.SetScanRecvLotUniNum:
      return { ...state, scanRecvLotUniNum: action.params };

    case PoRecvActionType.SetScanRecvRatio:
      return { ...state, scanRecvRatio: action.params };

    case PoRecvActionType.SetScanRecvRatioType:
      return { ...state, scanRecvRatioType: action.params };

    case PoRecvActionType.SetScanRecvScopeType:
      return { ...state, scanRecvScopeType: action.params };

    case PoRecvActionType.SetSelectedWarehouse:
      return { ...state, selectedWarehouse: action.params };

    case PoRecvActionType.SetTimeZone:
      return { ...state, timeZone: action.params };

    case PoRecvActionType.SetUomInfo:
      return { ...state, uomInfo: { ...action.params } };


    default:
      return state;
  }
};
